import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import AcademiaSelect from '../../common/AcademiaSelect';
import AdminCatalogImageField from './AdminCatalogImageField';
import AdminPagination from '../AdminPagination';
import { AcademicPanelCard, AcademicPanelTableWrap } from './AcademicPanelCard';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import {
  createAdminPartenaireSponsor,
  deleteAdminPartenaireSponsor,
  fetchAdminPartenairesSponsors,
  patchAdminPartenaireSponsor,
} from '../../../services/academiaAdminService';
import { uploadAdminPartnerContract } from '../../../services/adminService';
import { resolveBrandingLogoUrl } from '../../../utils/brandingUrl';

const INPUT_CLASS =
  'mt-1 w-full rounded-xl border border-outline-variant/25 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/15';
const FIELD_LABEL = 'text-xs font-bold uppercase tracking-wide text-slate-700';

type Categorie = 'PARTENAIRE' | 'SPONSOR';

type Row = {
  id: string;
  nom: string;
  urlLogo: string;
  urlSite?: string | null;
  categorie: Categorie;
  ordre: number;
  estActif: boolean;
  montantChiffreAffaires?: number | null;
  dateDebutContrat?: string | null;
  dateFinContrat?: string | null;
  dureeContratMois?: number | null;
  urlContrat?: string | null;
  detailsContrat?: string | null;
};

type FormState = {
  nom: string;
  urlLogo: string;
  urlSite: string;
  categorie: Categorie;
  ordre: string;
  estActif: boolean;
  montantChiffreAffaires: string;
  dateDebutContrat: string;
  dateFinContrat: string;
  dureeContratMois: string;
  urlContrat: string;
  detailsContrat: string;
};

const EMPTY_FORM: FormState = {
  nom: '',
  urlLogo: '',
  urlSite: '',
  categorie: 'PARTENAIRE',
  ordre: '0',
  estActif: true,
  montantChiffreAffaires: '',
  dateDebutContrat: '',
  dateFinContrat: '',
  dureeContratMois: '',
  urlContrat: '',
  detailsContrat: '',
};

function categorieLabel(value: Categorie) {
  return value === 'SPONSOR' ? 'Sponsor' : 'Partenaire';
}

function formFromRow(row: Row): FormState {
  return {
    nom: row.nom,
    urlLogo: row.urlLogo,
    urlSite: row.urlSite ?? '',
    categorie: row.categorie,
    ordre: String(row.ordre ?? 0),
    estActif: row.estActif,
    montantChiffreAffaires:
      row.montantChiffreAffaires == null ? '' : String(row.montantChiffreAffaires),
    dateDebutContrat: row.dateDebutContrat ?? '',
    dateFinContrat: row.dateFinContrat ?? '',
    dureeContratMois: row.dureeContratMois == null ? '' : String(row.dureeContratMois),
    urlContrat: row.urlContrat ?? '',
    detailsContrat: row.detailsContrat ?? '',
  };
}

export default function AcademiaPartenairesSponsorsPanel() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState<'ALL' | Categorie>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [contractUploadBusy, setContractUploadBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await fetchAdminPartenairesSponsors();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les partenaires et sponsors.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load());

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return rows.filter((row) => {
      if (filter !== 'ALL' && row.categorie !== filter) return false;
      if (statusFilter === 'ACTIVE' && !row.estActif) return false;
      if (statusFilter === 'INACTIVE' && row.estActif) return false;
      if (!normalizedSearch) return true;

      return [
        row.nom,
        row.urlSite,
        row.urlLogo,
        categorieLabel(row.categorie),
        row.urlContrat,
        row.detailsContrat,
        row.dateDebutContrat,
        row.dateFinContrat,
        row.dureeContratMois,
        row.montantChiffreAffaires,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    });
  }, [filter, rows, searchQuery, statusFilter]);

  const counts = useMemo(
    () => ({
      total: rows.length,
      partenaires: rows.filter((row) => row.categorie === 'PARTENAIRE').length,
      sponsors: rows.filter((row) => row.categorie === 'SPONSOR').length,
      actifs: rows.filter((row) => row.estActif).length,
      inactifs: rows.filter((row) => !row.estActif).length,
    }),
    [rows],
  );
  const pagination = useAdminTablePagination(filteredRows);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (row: Row) => {
    setEditingId(row.id);
    setForm(formFromRow(row));
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const openDuplicate = (row: Row) => {
    setEditingId(null);
    setForm({
      ...formFromRow(row),
      nom: `${row.nom} (copie)`,
    });
    setShowForm(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const montant =
        form.montantChiffreAffaires.trim() === ''
          ? null
          : (() => {
              const n = Number(form.montantChiffreAffaires);
              return Number.isFinite(n) ? n : null;
            })();

      const dureeMois =
        form.dureeContratMois.trim() === ''
          ? null
          : (() => {
              const n = Number(form.dureeContratMois);
              return Number.isFinite(n) ? n : null;
            })();

      const payload = {
        nom: form.nom.trim(),
        urlLogo: form.urlLogo.trim(),
        urlSite: form.urlSite.trim() || null,
        categorie: form.categorie,
        ordre: Number(form.ordre) || 0,
        estActif: form.estActif,
        montantChiffreAffaires: montant,
        dateDebutContrat: form.dateDebutContrat.trim() || null,
        dateFinContrat: form.dateFinContrat.trim() || null,
        dureeContratMois: dureeMois,
        urlContrat: form.urlContrat.trim() || null,
        detailsContrat: form.detailsContrat.trim() || null,
      };

      if (editingId) {
        await patchAdminPartenaireSponsor(editingId, payload);
        setToast('Partenaire ou sponsor mis à jour.');
      } else {
        await createAdminPartenaireSponsor(payload);
        setToast('Partenaire ou sponsor ajouté.');
      }

      closeForm();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: Row) => {
    setSaving(true);
    setError(null);
    try {
      await deleteAdminPartenaireSponsor(row.id);
      setToast('Élément supprimé.');
      setDeleteTarget(null);
      if (editingId === row.id) closeForm();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleContractFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setContractUploadBusy(true);
    setError(null);
    try {
      const data = await uploadAdminPartnerContract(file);
      const url = String(data?.url || data?.urlFichier || data?.path || '');
      if (!url) throw new Error('URL du contrat introuvable après upload.');
      setForm((current) => ({ ...current, urlContrat: url }));
      setToast('Contrat téléversé.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Téléversement du contrat impossible.');
    } finally {
      setContractUploadBusy(false);
    }
  };

  const toggleActif = async (row: Row) => {
    setSaving(true);
    setError(null);
    try {
      await patchAdminPartenaireSponsor(row.id, { estActif: !row.estActif });
      setToast(row.estActif ? 'Élément masqué sur le site public.' : 'Élément visible sur le site public.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Mise à jour impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageShell
      title="Partenaires & sponsors"
      description="Gérez les logos affichés sur la page d'accueil publique (bandeau de confiance en bas de page). Seuls les éléments actifs avec un logo sont visibles. Distinct des partenaires employabilité (module Excellence)."
      isLoading={loading}
      isError={Boolean(error) && rows.length === 0}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      primaryAction={
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-primary shadow-md hover:bg-white/95"
        >
          <LucideSymbolIcon name="add" tone="primary" size={18} />
          Ajouter
        </button>
      }
      sideCard={{
        label: 'Visibles sur le site',
        value: String(counts.actifs),
        helper: `${counts.total} total · ${counts.partenaires} partenaire(s) · ${counts.sponsors} sponsor(s)`,
        sideLinks: [
          { label: 'Voir la page d’accueil', to: '/', icon: 'public' },
          { label: 'Site & QR', to: '/admin/site', icon: 'link' },
        ],
      }}
    >
      <AdminToast message={toast} variant="success" onClose={() => setToast(null)} />
      <DeleteConfirmModal
        open={Boolean(deleteTarget)}
        title="Supprimer ce partenaire ou sponsor ?"
        description={
          deleteTarget ? (
            <>
              Vous allez supprimer <strong>{deleteTarget.nom}</strong>.
              <br />
              Cette action retire définitivement son logo et son affichage public.
            </>
          ) : undefined
        }
        isConfirming={saving}
        onCancel={() => {
          if (!saving) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget) {
            void handleDelete(deleteTarget);
          }
        }}
      />
      {error && rows.length > 0 ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      ) : null}

      <div className="space-y-6">
        {showForm ? (
          <AcademicPanelCard
            icon={editingId ? 'edit' : 'add_circle'}
            title={editingId ? 'Modifier le partenaire / sponsor' : 'Nouveau partenaire / sponsor'}
            subtitle={
              editingId
                ? 'Modifiez le logo, le lien, la visibilité et les informations contractuelles.'
                : 'Ajoutez un partenaire ou sponsor. Le logo est obligatoire.'
            }
          >
            <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
              <label className="block">
                <span className={FIELD_LABEL}>Nom</span>
                <input
                  className={INPUT_CLASS}
                  value={form.nom}
                  onChange={(e) => setForm((current) => ({ ...current, nom: e.target.value }))}
                  required
                />
              </label>

              <label className="block">
                <span className={FIELD_LABEL}>Catégorie</span>
                <AcademiaSelect
                  className="mt-1"
                  value={form.categorie}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, categorie: e.target.value as Categorie }))
                  }
                >
                  <option value="PARTENAIRE">Partenaire</option>
                  <option value="SPONSOR">Sponsor</option>
                </AcademiaSelect>
              </label>

              <AdminCatalogImageField
                label="Logo"
                value={form.urlLogo}
                onChange={(urlLogo) => setForm((current) => ({ ...current, urlLogo }))}
                inputClass={INPUT_CLASS}
                onUploadError={(message) => setError(message)}
                helperText="Logo affiché sur la page d’accueil publique (PNG, JPEG ou WebP)."
                compact
              />

              <label className="block">
                <span className={FIELD_LABEL}>Site web (optionnel)</span>
                <input
                  className={INPUT_CLASS}
                  value={form.urlSite}
                  onChange={(e) => setForm((current) => ({ ...current, urlSite: e.target.value }))}
                  placeholder="https://…"
                />
              </label>

              <label className="block">
                <span className={FIELD_LABEL}>Chiffre d’affaires (CDF, optionnel)</span>
                <input
                  className={INPUT_CLASS}
                  type="number"
                  step="0.01"
                  value={form.montantChiffreAffaires}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, montantChiffreAffaires: e.target.value }))
                  }
                  placeholder="ex. 1500000"
                />
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className={FIELD_LABEL}>Date début contrat</span>
                  <input
                    className={INPUT_CLASS}
                    type="date"
                    value={form.dateDebutContrat}
                    onChange={(e) =>
                      setForm((current) => ({ ...current, dateDebutContrat: e.target.value }))
                    }
                  />
                </label>

                <label className="block">
                  <span className={FIELD_LABEL}>Date fin contrat</span>
                  <input
                    className={INPUT_CLASS}
                    type="date"
                    value={form.dateFinContrat}
                    onChange={(e) =>
                      setForm((current) => ({ ...current, dateFinContrat: e.target.value }))
                    }
                  />
                </label>
              </div>

              <label className="block">
                <span className={FIELD_LABEL}>Durée contrat (mois, optionnel)</span>
                <input
                  className={INPUT_CLASS}
                  type="number"
                  value={form.dureeContratMois}
                  onChange={(e) => setForm((current) => ({ ...current, dureeContratMois: e.target.value }))}
                  placeholder="ex. 12"
                />
              </label>

              <label className="block">
                <span className={FIELD_LABEL}>Contrat (fichier, optionnel)</span>
                <div className="mt-1 rounded-xl border border-outline-variant/20 bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-primary/25 bg-primary/5 px-3.5 py-2 text-xs font-bold text-primary hover:bg-primary/10">
                      <LucideSymbolIcon name="upload_file" tone="primary" size={16} />
                      {contractUploadBusy ? 'Téléversement…' : 'Téléverser le contrat'}
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                        className="hidden"
                        onChange={(e) => void handleContractFileChange(e)}
                      />
                    </label>
                    {form.urlContrat ? (
                      <button
                        type="button"
                        onClick={() => setForm((current) => ({ ...current, urlContrat: '' }))}
                        className="rounded-xl border border-outline-variant/25 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Retirer
                      </button>
                    ) : null}
                  </div>
                  <input
                    className={`${INPUT_CLASS} mt-3`}
                    value={form.urlContrat}
                    onChange={(e) => setForm((current) => ({ ...current, urlContrat: e.target.value }))}
                    placeholder="/uploads/public/... ou URL externe"
                  />
                </div>
              </label>

              <label className="block">
                <span className={FIELD_LABEL}>Détails contrat (optionnel)</span>
                <textarea
                  className={`${INPUT_CLASS} min-h-[5rem] py-3`}
                  value={form.detailsContrat}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, detailsContrat: e.target.value }))
                  }
                  placeholder="Référence, clauses importantes, etc."
                />
              </label>

              <label className="block">
                <span className={FIELD_LABEL}>Ordre d&apos;affichage</span>
                <input
                  className={INPUT_CLASS}
                  type="number"
                  value={form.ordre}
                  onChange={(e) => setForm((current) => ({ ...current, ordre: e.target.value }))}
                />
              </label>

              <label className="flex items-center gap-3 rounded-xl border border-outline-variant/20 bg-slate-50 px-4 py-3">
                <input
                  type="checkbox"
                  checked={form.estActif}
                  onChange={(e) => setForm((current) => ({ ...current, estActif: e.target.checked }))}
                />
                <span className="text-sm font-medium text-slate-800">Visible sur la page d&apos;accueil publique</span>
              </label>

              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60"
                >
                  <LucideSymbolIcon name="save" tone="inherit" size={18} className="text-white" />
                  {editingId ? 'Enregistrer' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-xl border border-outline-variant/25 px-4 py-2.5 text-sm font-semibold text-slate-700"
                >
                  Annuler
                </button>
              </div>
            </form>
          </AcademicPanelCard>
        ) : null}

        <AcademicPanelCard
          icon="handshake"
          title="Liste des partenaires & sponsors"
          subtitle={`${filteredRows.length} résultat(s) · ordre croissant puis nom. Les éléments inactifs restent en base mais ne s'affichent pas sur le site.`}
          badge={
            <div className="flex flex-wrap gap-2">
              {(['ALL', 'PARTENAIRE', 'SPONSOR'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFilter(value)}
                  className={`rounded-full px-3 py-1 text-xs font-bold ${
                    filter === value ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {value === 'ALL' ? 'Tous' : categorieLabel(value)}
                </button>
              ))}
            </div>
          }
          flush
        >
          <div className="flex flex-col gap-3 border-b border-outline-variant/15 bg-slate-50/70 px-4 py-4 sm:px-5">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto]">
              <label className="block">
                <span className={FIELD_LABEL}>Recherche</span>
                <div className="relative">
                  <LucideSymbolIcon
                    name="search"
                    tone="inherit"
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    className={`${INPUT_CLASS} pl-9`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Nom, catégorie, lien ou URL du logo"
                  />
                </div>
              </label>

              <label className="block min-w-[11rem]">
                <span className={FIELD_LABEL}>Statut</span>
                <AcademiaSelect
                  className="mt-1 min-w-[11rem]"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'ACTIVE' | 'INACTIVE')}
                >
                  <option value="ALL">Tous</option>
                  <option value="ACTIVE">Actifs</option>
                  <option value="INACTIVE">Inactifs</option>
                </AcademiaSelect>
              </label>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setFilter('ALL');
                    setStatusFilter('ALL');
                  }}
                  className="w-full rounded-xl border border-outline-variant/25 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Réinitialiser
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                {counts.total} total
              </span>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                {counts.actifs} actif(s)
              </span>
              <span className="rounded-full bg-slate-200/80 px-3 py-1 text-xs font-semibold text-slate-700">
                {counts.inactifs} inactif(s)
              </span>
            </div>
          </div>

          <AcademicPanelTableWrap
            empty={
              filteredRows.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-on-surface-variant">
                  Aucun partenaire ou sponsor enregistré.
                </div>
              ) : undefined
            }
          >
            {filteredRows.length > 0 ? (
              <table className="academic-panel-table">
                <thead>
                  <tr>
                    <th>Logo</th>
                    <th>Nom</th>
                    <th>Catégorie</th>
                    <th>CA (CDF)</th>
                    <th>Contrat</th>
                    <th>Statut</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.paginatedItems.map((row) => {
                    const logoSrc = resolveBrandingLogoUrl(row.urlLogo);
                    return (
                      <tr key={row.id}>
                        <td>
                          <div className="flex h-12 w-20 items-center justify-center rounded-lg border border-outline-variant/15 bg-white p-2">
                            {logoSrc ? (
                              <img src={logoSrc} alt="" className="max-h-8 max-w-full object-contain" />
                            ) : (
                              <LucideSymbolIcon name="image" tone="inherit" size={18} className="text-slate-400" />
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="font-semibold text-slate-900">{row.nom}</div>
                          {row.urlSite ? (
                            <a
                              href={row.urlSite}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline"
                            >
                              Site web
                            </a>
                          ) : (
                            <span className="text-xs text-on-surface-variant">Sans lien</span>
                          )}
                        </td>
                        <td>{categorieLabel(row.categorie)}</td>
                        <td>{row.montantChiffreAffaires != null ? Number(row.montantChiffreAffaires).toLocaleString('fr-FR') : '—'}</td>
                        <td>
                          {(() => {
                            const now = Date.now();
                            const debutOk = row.dateDebutContrat
                              ? new Date(row.dateDebutContrat).getTime() <= now
                              : true;
                            const finOk = row.dateFinContrat ? new Date(row.dateFinContrat).getTime() >= now : true;
                            const contratValide = debutOk && finOk;
                            const badgeClasses = contratValide
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-900';
                            const finLabel = row.dateFinContrat
                              ? new Date(row.dateFinContrat).toLocaleDateString('fr-FR')
                              : '—';
                            return (
                              <div className="flex min-w-[10rem] flex-col gap-1">
                                <span className={`rounded-full px-2 py-1 text-[11px] font-bold ${badgeClasses}`}>
                                  {contratValide ? 'Contrat valide' : 'Contrat expiré'}
                                </span>
                                <span className="text-xs text-on-surface-variant">Jusqu’au {finLabel}</span>
                                {row.urlContrat ? (
                                  <a
                                    href={row.urlContrat}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-semibold text-primary hover:underline"
                                  >
                                    Ouvrir
                                  </a>
                                ) : null}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          <div className="flex flex-col items-start gap-1">
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => void toggleActif(row)}
                              className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                                row.estActif ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {row.estActif ? 'Actif' : 'Inactif'}
                            </button>
                            {row.estActif && row.dateFinContrat && new Date(row.dateFinContrat).getTime() < Date.now() ? (
                              <span className="text-[11px] font-semibold text-amber-900 bg-amber-100 rounded-full px-2 py-1">
                                Non visible (contrat expiré)
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/partenaires-sponsors/${row.id}`)}
                              className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10"
                            >
                              Voir
                            </button>
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="rounded-lg border border-outline-variant/25 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Modifier
                            </button>
                            <button
                              type="button"
                              onClick={() => openDuplicate(row)}
                              className="rounded-lg border border-outline-variant/25 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Dupliquer
                            </button>
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => setDeleteTarget(row)}
                              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}
          </AcademicPanelTableWrap>
          <AdminPagination
            className="border-t border-outline-variant/15 px-4 py-3"
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
            summary={pagination.summary}
          />
        </AcademicPanelCard>
      </div>
    </AdminPageShell>
  );
}
